import { useState } from "react";
import api from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { PageHeader, Panel, StatusBadge, Loading, MockBanner } from "@/components/common";
import { Translate, Play, ArrowsClockwise, ChatCircleText, Sparkle, Spinner, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const LANGS = [
  { name: "English", flag: "🇬🇧", master: true, status: "APPROVED" },
  { name: "Turkish", flag: "🇹🇷", status: "READY FOR REVIEW" },
  { name: "Bulgarian", flag: "🇧🇬", status: "GENERATING" },
  { name: "Spanish", flag: "🇪🇸", status: "NOT CREATED" },
  { name: "German", flag: "🇩🇪", status: "NOT CREATED" },
  { name: "French", flag: "🇫🇷", status: "NOT CREATED" },
];
const TARGETS = ["Turkish", "Bulgarian", "Spanish", "German", "French"];

export default function Localization() {
  const { data: eps, loading } = useFetch("/episodes");
  const [active, setActive] = useState(0);
  const [source, setSource] = useState("Bobo! Today we explore Dinosaur Island! Come on, it will be amazing!");
  const [busy, setBusy] = useState(false);
  const [translations, setTranslations] = useState(null);
  const [approved, setApproved] = useState({});

  const translate = async () => {
    if (!source.trim()) { toast.error("Enter text to localize"); return; }
    setBusy(true); setTranslations(null);
    try {
      const { data } = await api.post("/localize", { text: source, languages: TARGETS });
      setTranslations(data.translations);
      toast.success("Localized with real AI (natural, not literal)");
    } catch { toast.error("Localization failed"); }
    finally { setBusy(false); }
  };

  if (loading || !eps) return <Loading />;
  const ep = eps[active];

  return (
    <div>
      <PageHeader icon={Translate} title="Localization Studio"
        subtitle="English is the master language. Studio AI localizes dialogue naturally for children — preserving meaning, personality, emotion and comedy — not literal translation."
        badge={<StatusBadge status="LIVE" />} />

      <Panel className="mb-8">
        <div className="flex items-center gap-2 mb-3 text-sm text-zinc-400"><Sparkle size={16} weight="fill" className="text-[#00E5FF]" /> Real text localization (works now)</div>
        <textarea data-testid="localize-source" value={source} onChange={(e) => setSource(e.target.value)} rows={2}
          className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3 text-white outline-none focus:border-[#00E5FF]/50 resize-none mb-3" />
        <button data-testid="localize-btn" onClick={translate} disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-magic px-5 py-2.5 font-semibold text-black hover:opacity-90 transition-opacity disabled:opacity-60">
          {busy ? <Spinner size={18} className="animate-spin" /> : <Translate size={18} weight="fill" />} {busy ? "Localizing…" : "Localize into 5 languages"}
        </button>
        {translations && (
          <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="localize-results">
            {Object.entries(translations).map(([lang, txt]) => (
              <div key={lang} className="rounded-xl border border-white/8 bg-black/20 p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-white">{lang}</span>
                  <button onClick={() => setApproved((a) => ({ ...a, [lang]: !a[lang] }))}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${approved[lang] ? "bg-[#00FF9D]/15 text-[#00FF9D] border border-[#00FF9D]/40" : "border border-white/10 text-zinc-400"}`}>
                    {approved[lang] ? <><Check size={11} weight="bold" /> Approved</> : "Approve"}
                  </button>
                </div>
                <p className="text-sm text-zinc-200">{txt || "—"}</p>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <MockBanner>Voice & lip-sync localization stay NOT CONNECTED until a local/cloud voice engine is attached. Text localization above is REAL.</MockBanner>

      <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
        {eps.map((e, i) => (
          <button key={e.id} onClick={() => setActive(i)} data-testid={`loc-ep-${e.id}`}
            className={`shrink-0 rounded-full px-4 py-2 text-sm border transition-colors ${i === active ? "border-[#00E5FF]/50 bg-[#00E5FF]/8 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>{e.title}</button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {LANGS.map((l) => (
          <Panel key={l.name} data-testid={`loc-lang-${l.name}`} className={l.master ? "border-[#00E5FF]/30" : ""}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{l.flag}</span>
                <div>
                  <div className="font-display font-bold text-white">{l.name}</div>
                  {l.master && <span className="text-[10px] font-mono text-[#00E5FF]">MASTER</span>}
                </div>
              </div>
              <StatusBadge status={l.status} />
            </div>
            <div className="rounded-lg bg-black/20 border border-white/5 p-3 mb-3">
              <div className="text-[11px] text-zinc-500 mb-1 flex items-center gap-1"><ChatCircleText size={12} weight="duotone" /> The Wayfinder</div>
              <p className="text-sm text-white">{sampleLine(l.name)}</p>
            </div>
            <div className="flex gap-2">
              {(l.status === "APPROVED" || l.status === "READY FOR REVIEW") && (
                <button onClick={() => toast(`Watch ${l.name} version (Phase 2 player).`)} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-white/5 border border-white/10 py-2 text-xs text-white hover:border-[#00E5FF]/40 transition-colors"><Play size={13} weight="fill" /> Watch</button>
              )}
              {!l.master && (
                <button onClick={() => toast(`Regenerating ${l.name} dialogue only.`)} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3 py-2 text-xs text-white hover:border-white/30 transition-colors"><ArrowsClockwise size={13} /></button>
              )}
            </div>
            {!l.master && <p className="mt-3 text-[11px] text-zinc-500">Say: "The Wayfinder action beat should feel more urgent in {l.name}" to regenerate just that dialogue.</p>}
          </Panel>
        ))}
      </div>
      <p className="mt-6 text-xs text-zinc-500">Lip sync adaptation to localized speech connects in Phase 2 — unsupported functionality is clearly labelled, never faked.</p>
    </div>
  );
}

function sampleLine(lang) {
  const map = {
    English: "Bobo! Today we explore Dinosaur Island!",
    Turkish: "Bobo! Bugün Dinozor Adası'nı keşfediyoruz!",
    Bulgarian: "Бобо! Днес разглеждаме Динозавърския остров!",
    Spanish: "¡Bobo! ¡Hoy exploramos la Isla de los Dinosaurios!",
    German: "Bobo! Heute erkunden wir die Dinosaurierinsel!",
    French: "Bobo ! Aujourd'hui on explore l'Île aux Dinosaures !",
  };
  return map[lang] || "—";
}
